import { useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useTranslations } from "@jti/ui";

import { useOrderItemsMappedToSummaryItems } from "@checkout-v2/features/ItemsSummary/hooks";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import { PaymentSummaryView } from "../../components/PaymentSummaryView";
import { getFormattedPaymentDiscounts } from "../../utils/getFormattedPaymentDiscounts";

type OrderPaymentSummaryContainerProps = Partial<{
  orderNumber: string;
}>;

export const OrderPaymentSummaryContainer = ({ orderNumber }: OrderPaymentSummaryContainerProps) => {
  const { __ } = useTranslations();
  const { showPricesWithTaxExcluded = false } = useConfig();
  const checkoutConfiguration = useCheckoutV2Configuration();

  const { data: orderData, isLoading, isUninitialized } = useCustomerOrder(orderNumber);
  const { summaryItems } = useOrderItemsMappedToSummaryItems(orderData?.items);

  const discounts = useMemo(
    () => getFormattedPaymentDiscounts(orderData?.prices?.discounts, __),
    [orderData?.prices?.discounts, __]
  );

  if (isUninitialized || isLoading) {
    return <PaymentSummaryView isLoading={true} />;
  }

  if (!orderData?.prices) {
    return null;
  }

  const { appliedTaxes, grandTotal, subtotal, shipping, appliedCampaign, subtotalIncludingTax } = orderData.prices;
  const priceToUse = showPricesWithTaxExcluded ? subtotal : (subtotalIncludingTax ?? subtotal);
  const taxValue = appliedTaxes?.length
    ? {
        value: appliedTaxes.reduce((sum, tax) => sum + tax.amount.value, 0),
        currency: grandTotal?.currency || "",
      }
    : undefined;

  const loyaltyDiscount = appliedCampaign?.loyalty_discount_amount
    ? {
        label: __("cart.prices.loyalty.points_redemption"),
        value: appliedCampaign.loyalty_discount_amount,
        currency: priceToUse?.currency ?? "",
      }
    : undefined;

  return (
    <PaymentSummaryView
      title={__("order.payment_summary.title")}
      subtotal={priceToUse}
      shipping={shipping}
      total={grandTotal}
      appliedTaxes={appliedTaxes}
      taxValue={taxValue}
      taxMessage={checkoutConfiguration.taxInfo?.visible ? checkoutConfiguration.taxInfo.message : undefined}
      discounts={discounts}
      loyaltyDiscount={loyaltyDiscount}
      summaryItems={summaryItems}
    />
  );
};
